import {
  BAY_LEVEL_FORE_AFT_FIELDS,
  STAF_INTERMEDIATE_BAY_LEVEL_FIELDS,
  STAF_INTERMEDIATE_ROW_FIELDS,
} from "../structure/consts";
import {
  FIXABLE_BAY_LEVEL_CODES,
  IBayLevelStructureFinding,
  bayLevelStructureValidatorFn,
} from "../structure/bayLevelStructureValidator";
import { LASHING_BRIDGE_SIDES, convertLegacyLashingBridgeSide } from "@tedivo/tedivo-bay-grid-pure";
import { isPlainObject, isUnsetEnum } from "../structure/patterns";

import { IOpenVesselDefinitionV1 } from "open-vessel-definition";
import { cloneObject } from "@tedivo/tedivo-pure-helpers";

export interface IFixBayLevelStructureResult {
  /** The vessel with the fixable findings resolved. The input object when nothing changed */
  json: IOpenVesselDefinitionV1;
  /** What was fixed: `size` and `isoRow` set to their keys, STAF leftovers removed, OVD 1.2.0 lashing bridges converted, unset enums removed */
  fixed: IBayLevelStructureFinding[];
  /** What `bayLevelStructureValidator` reports and this helper does not touch */
  notFixed: IBayLevelStructureFinding[];
  changed: boolean;
}

/**
 * Resolves the lossless findings of `bayLevelStructureValidator`: an `infoByContLength`
 * or `rowInfoByLength` entry whose `size` differs from its key gets the key; a
 * `perRowInfo.each` entry whose `isoRow` differs from its key gets the key; fields
 * left over from a STAF conversion are removed; lashing bridge sides written with the
 * OVD 1.2.0 flat shape are converted to the 1.3.0 shape (a side that was off is removed,
 * and so is `lashingBridges` when nothing is left); `pairedBay`, `reeferPlugs` and `doors`
 * written as `0` or `null` ("not set") are removed. The input is not modified.
 *
 * Bad keys, duplicates and wrong types are left alone: there is no single right answer.
 */
export function fixBayLevelStructure(json: IOpenVesselDefinitionV1): IFixBayLevelStructureResult {
  const { findings } = bayLevelStructureValidatorFn({
    data: { json, lcgVcgTcgAndPairings: undefined, lcgsBy20Bay: undefined },
  }).result;

  const fixed = findings.filter((f) => FIXABLE_BAY_LEVEL_CODES.includes(f.code));
  const notFixed = findings.filter((f) => !FIXABLE_BAY_LEVEL_CODES.includes(f.code));

  if (fixed.length === 0) return { json, fixed, notFixed, changed: false };

  const newJson = cloneObject(json);

  (newJson.baysData as unknown[]).forEach((entry) => {
    if (!isPlainObject(entry)) return;

    fixLengthDictionary(entry["infoByContLength"]);

    const each = isPlainObject(entry["perRowInfo"]) ? entry["perRowInfo"]["each"] : undefined;
    if (isPlainObject(each)) {
      Object.keys(each).forEach((row) => {
        const r = each[row];
        if (!isPlainObject(r)) return;
        if (r["isoRow"] !== row) r["isoRow"] = row;
        fixLengthDictionary(r["rowInfoByLength"]);
        STAF_INTERMEDIATE_ROW_FIELDS.forEach((f) => delete r[f]);
      });
    }

    STAF_INTERMEDIATE_BAY_LEVEL_FIELDS.forEach((f) => delete entry[f]);

    BAY_LEVEL_FORE_AFT_FIELDS.forEach((f) => {
      if (isUnsetEnum(entry[f])) delete entry[f];
    });

    fixLegacyLashingBridges(entry);
  });

  return { json: newJson, fixed, notFixed, changed: true };
}

function fixLengthDictionary(dict: unknown): void {
  if (!isPlainObject(dict)) return;
  Object.keys(dict).forEach((k) => {
    const entry = dict[k];
    const n = Number(k);
    if (isPlainObject(entry) && Number.isFinite(n) && entry["size"] !== n) entry["size"] = n;
  });
}

/** Converts the lashing bridge sides written with the OVD 1.2.0 flat shape. Unconvertible sides are left alone */
function fixLegacyLashingBridges(entry: Record<string, unknown>): void {
  const lb = entry["lashingBridges"];
  if (!isPlainObject(lb)) return;

  LASHING_BRIDGE_SIDES.forEach((side) => {
    const converted = convertLegacyLashingBridgeSide(lb, side);
    if (converted === undefined || converted === "unconvertible") return;

    delete lb[`${side}Tiers`];
    delete lb[`${side}Lcg`];
    if (converted === null) delete lb[side];
    else lb[side] = converted;
  });

  if (Object.keys(lb).length === 0) delete entry["lashingBridges"];
}
